<template>
  <section class="part devicePart" :class="[props.type, props.type + 'On', props.type + deviceDirection]">
    <audio v-if="device === 'speaker'" id="alarmAudio" ref="tester">
      <source :src="accountStore.baseUrl + '/assets/audio/alarm.mp3'" />
      <source :src="accountStore.baseUrl + '/assets/audio/alarm.ogg'" />
    </audio>
    <h3 :class="[currentLabel ? 'on' : 'off']">
      <app-icon :icon="'connectivity-' + iconName" class="deviceIcon" />
      <span class="type visuallyHidden">{{ props.type }} {{ props.direction }}</span>
    </h3>
    <div v-click-outside="{ handler: handleClickOutside }" class="deviceRow">
      <p class="currentDevice" @click="showList = !showList">
        {{ currentLabel }}
      </p>
      <div class="devices">
        <button class="devicesToggle" :class="{ open: showList }" @click="showList = !showList">
          <span class="text visuallyHidden">Show {{ props.type }} devices</span>
          <app-icon icon="down-arrow" />
        </button>
        <transition name="basic">
          <ul v-if="showList" class="deviceList dropdown">
            <li v-for="(x, i) in devicesList" :key="'device-' + i">
              <button class="option" :class="[x.deviceId === currentDeviceId ? 'current' : '']" @click.prevent="send(x.deviceId, x.label)">
                {{ x.label.replace(/\s*\(.*?\)\s*/g, '') }}
              </button>
            </li>
            <li v-if="props.canTurnOff && devicesList && props.currentId" class="turnOff">
              <button class="disable" @click.prevent="disable">Turn off {{ device }}</button>
            </li>
            <li v-if="!devicesList || devicesList.length === 0"><span class="noDevices">No devices available</span></li>
          </ul>
        </transition>
      </div>
      <div v-if="props.type === 'audio' && props.direction === 'in'" class="meter" id="audio-meter">
        <div id="audio-meter-level" class="bar" :style="{ width: volumeLevel * 100 + '%' }" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { storeToRefs } from 'pinia';
import AppIcon from '@/components/AppIcon.vue';
import utils from '@/resources/utils.js';
import { usePageStore } from '@/store/pinia/page';
import { useAccountStore } from '@/store/pinia/account';
import { useMediaStore } from '@/store/pinia/media';
import { useUsersStore } from '@/store/pinia/users';
import { useStorage } from 'vue3-storage';
const localStorage = useStorage({ namespace: '', storage: 'local' });

const { $ee } = getCurrentInstance().appContext.config.globalProperties;

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  currentId: {
    // Accepts a string representing the device ID or null when no device is selected
    type: [String, null],
    required: true,
  },
  on: {
    type: Boolean,
    required: true,
  },
  direction: {
    type: String,
    default: 'in',
  },
  device: {
    type: String,
    required: true,
  },
  canTurnOff: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change']);
const pageStore = usePageStore();
const accountStore = useAccountStore();
const mediaStore = useMediaStore();
const usersStore = useUsersStore();

const showList = ref(false);
const volumeLevel = ref(0);
const canChange = ref(true);

const tester = ref(null);

onMounted(() => {
  if (props.device === 'microphone') {
    $ee.on('api:setVolumeTestMeter', setVolumeLevel);
  }
  if (props.device === 'speaker') {
    try {
      tester.value.setSinkId('default');
    } catch (error) {
      canChange.value = false;
    }
  }
});

function handleClickOutside() {
  showList.value = false;
}

onUnmounted(() => {
  if (props.device === 'microphone') {
    $ee.off('api:setVolumeTestMeter', setVolumeLevel);
  }
});

const { devices } = storeToRefs(mediaStore);
const { iAmMuted, iAmSysMuted, me } = storeToRefs(usersStore);
const { current: currentPage } = storeToRefs(pageStore);
const isOn = computed(() => props.on);
const isConnectivityCheckPage = computed(() => currentPage.value.pageName === 'connectivity');
const deviceDirection = computed(() => (props.direction === 'in' ? 'Input' : 'Output'));
const iconName = computed(() => (props.direction === 'in' ? props.type : props.type + deviceDirection.value));
const currentLabel = computed(() => currentDevice.value || `No ${props.device} selected`);
const devicesList = computed(() => {
  if (!canChange.value) {
    return [
      {
        deviceId: 'default',
        label: 'Default/Built-in output device',
      },
    ];
  } else {
    return devices.value
      .filter((x) => x.kind === props.type + deviceDirection.value.toLowerCase())
      .map((d) => {
        return {
          deviceId: d.deviceId,
          label: utils.cleanDeviceLabel(d.label),
        };
      });
  }
});

const currentDeviceId = computed(() => {
  if (!canChange.value) {
    return 'default';
  } else {
    return props.currentId;
  }
});

const currentDevice = computed(() => {
  let n = devicesList.value.find((x) => {
    return x.deviceId === currentDeviceId.value;
  });

  if (!n) {
    return false;
  } else {
    return utils.cleanDeviceLabel(n.label);
  }
});

watch(isOn, (nv) => {
  if (props.type === 'audio' && !nv) {
    volumeLevel.value = 0;
  }
});

function send(deviceId) {
  emit('change', deviceId);
  if (props.type === 'audio' && props.direction === 'in') {
    localStorage.setStorageSync('user:audioSource', deviceId);
  }
  if (props.type === 'video') {
    localStorage.setStorageSync('user:videoSource', deviceId);
  }
  if (props.type === 'audio' && props.direction === 'out') {
    localStorage.setStorageSync('user:audioOutputSource', deviceId);
  }
  showList.value = false;
}

function disable() {
  emit('change', null);
  showList.value = false;
}

// Removed hideList function as its logic is already handled by directly modifying showList.value

function setVolumeLevel(level) {
  if (props.on && (isConnectivityCheckPage.value || (me.value && !iAmMuted.value && !iAmSysMuted.value))) {
    volumeLevel.value = level;
  }
}
</script>

<style lang="scss" scoped>
section.devicePart {
  width: auto;
  position: relative;
  height: rem(21px);
}

.devices {
  margin-bottom: 3em;
  position: absolute;
  z-index: 1;
  top: rem(8px);
  right: rem(-1px);
}

.currentDevice {
  @include type-size('x-small');
  line-height: div(15, 12);
  font-weight: 100;
  position: absolute;
  width: rem(355px);
  left: rem(44px);
  cursor: pointer;
  &::after {
    position: absolute;
    display: block;
    content: '';
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to right, var(--c__bg-alt-0) 0%, var(--c__bg-alt-0) rem(130px), var(--c__bg-alt-100) rem(160px));
  }
}

.deviceIcon {
  position: absolute;
  left: 0;
  :deep(svg) {
    width: 100%;
    display: block;
  }
}

.videoInput {
  z-index: 4;
  .currentDevice {
    top: rem(3px);
  }
  .devices {
    top: rem(3px);
  }
  .deviceIcon {
    width: rem(23px);
    top: rem(4.5px);
    left: rem(1px);
  }
  .devicesToggle {
    top: rem(0px);
  }
}

.audioInput {
  z-index: 3;
  margin: rem(18px) 0 rem(19px) 0;
  .currentDevice {
    top: -1px;
  }
  .devices {
    top: rem(5px);
  }
  .deviceIcon {
    width: rem(15px);
    left: rem(3px);
    top: rem(0.5px);
  }
  .devicesToggle {
    top: rem(1px);
  }
}
.meter {
  position: absolute;
  width: rem(171px);
  left: rem(44px);
  height: 2px;
  top: rem(20px);
  background: var(--c__placeholder-alt);
  .bar {
    height: 2px;
    background: var(--c__accent);
  }
}

.audioOutput {
  z-index: 2;
  .currentDevice {
    top: rem(3px);
  }
  .devices {
    top: rem(4px);
  }
  .deviceIcon {
    width: rem(23px);
    left: rem(2px);
    top: rem(2.5px);
  }
  .devicesToggle {
    top: rem(0px);
  }
}

.devicesToggle {
  z-index: 5;
  width: rem(21px);
  height: rem(12px);
  border: none;
  display: block;
  position: absolute;
  right: rem(2px);
  transform: rotateZ(0deg);
  transform-origin: center;
  transition: all 300ms cubic-bezier(0.45, 0, 0.55, 1);
  padding: 0;
  background: transparent;
  &.open {
    transform: rotateZ(180deg);
  }
  @include triggered {
    background: transparent;
  }
  svg {
    width: rem(26px);
    position: relative;
    top: 0;
    left: 0;
  }
}
.deviceList {
  transform-origin: 92% 18%;
  z-index: 4;
  list-style: none;
  top: rem(-16px);
  right: rem(-8px);
  padding-top: 0.5em;
  padding-right: rem(26px + 8px);
  width: rem(242px);
  button {
    @include type-size('tiny');
    padding: 0.5em;
    font-weight: 100;
    background: none;
    border: none;
    line-height: math.div(15, 12);
    text-align: left;
    &:hover,
    &:active,
    &:focus {
      color: var(--c__accent-alt);
    }
    &.current {
      color: var(--c__accent);
    }
  }
  li {
    padding-right: 0.5em;
  }
  li + li {
    margin-top: rem(15px);
  }
}
.turnOff {
  border-top: 1px solid var(--c__edges);
  margin-right: rem(-26px - 8px);
  margin-left: -0.5em;
  padding: 1em 0.5em 0.5em 0.5em;
}
.noDevices {
  display: inline-block;
  padding: 0.5em;
}
</style>
